/**
 * /mediaupload — mobile-first bulk uploader for photographers & videographers.
 *
 * Design goals:
 *   - Single-tap "Add photos / videos" button that opens the phone's native
 *     picker (Photos, Files, or Camera — role-appropriate)
 *   - Multiple files at once, uploaded in parallel (3 at a time)
 *   - Uploads go DIRECT to S3 via presigned PUT — the FastAPI server never
 *     touches the bytes, so we run at S3's speed, not the pod's
 *   - Live per-file progress bar
 *   - After upload, an inline row lets the shooter set title + price and
 *     ship it live in one tap
 *
 * Route: /mediaupload (mobile view) — auth-gated, redirects login → back here.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/auth/AuthContext";
import { api, BACKEND_URL } from "@/lib/api";
import { toast } from "sonner";
import {
  Upload, Camera, Video, Check, X, Loader2, DollarSign,
  AlertTriangle, LogOut, Save, Trash2, Image as ImageIcon,
} from "lucide-react";

const PHOTO_EXT = /^image\/(jpeg|jpg|png|webp|heic|heif)/i;
const MAX_CONCURRENT = 3;
// Photos are downscaled + re-encoded to JPEG before upload so a 6 MB
// iPhone shot becomes ~600 KB — 10× faster over LTE with zero visible
// quality loss for marketplace previews. Skipped for PNGs where users
// specifically want a lossless upload.
const PHOTO_MAX_EDGE = 2400;
const PHOTO_QUALITY = 0.85;

export default function MediaUpload() {
  const { user, loading, logout } = useAuth();
  const navigate = useNavigate();
  const fileRef = useRef(null);
  const activeRef = useRef(0);
  const [items, setItems] = useState([]); // {file, id, kind, progress, status, error, item_id, title, price, size}
  const [defaultPrice, setDefaultPrice] = useState(1000); // $10 default
  const [saving, setSaving] = useState({}); // {item_id: bool}

  const role = user?.role;
  const isPhoto = role === "photographer";
  const isVideo = role === "videographer";
  const isAdmin = role === "admin";
  const canUse = isPhoto || isVideo || isAdmin;

  useEffect(() => {
    if (!loading && !user) navigate("/login?next=/mediaupload");
  }, [loading, user, navigate]);

  if (loading) return <FullscreenMsg>Loading…</FullscreenMsg>;
  if (!user) return null; // navigate is in flight
  if (!canUse) return <RoleGate />;

  const acceptStr = isPhoto ? "image/*" : isVideo ? "video/*" : "image/*,video/*";
  // capture="environment" tells iOS/Android to prefer the rear camera when
  // the picker is invoked; users can still choose their gallery instead.
  const captureAttr = isPhoto ? "environment" : "environment";

  const pickFiles = () => fileRef.current?.click();

  const addFiles = (fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    const additions = files.map((f) => {
      const isImage = PHOTO_EXT.test(f.type);
      const kind = isImage ? "photo" : "video";
      // Reject cross-kind uploads (e.g. photographer picking a video)
      if (isPhoto && kind !== "photo") return null;
      if (isVideo && kind !== "video") return null;
      return {
        id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        file: f,
        kind,
        progress: 0,
        status: "queued", // queued | uploading | uploaded | saved | error
        error: "",
        item_id: "",
        title: cleanTitle(f.name),
        price: defaultPrice,
        size: f.size,
        thumb: kind === "photo" ? URL.createObjectURL(f) : "",
      };
    }).filter(Boolean);
    if (!additions.length) {
      toast.error(isPhoto ? "Photos only" : "Videos only");
      return;
    }
    setItems((prev) => [...additions, ...prev]);
    // Kick off the upload queue after state has settled
    setTimeout(() => runQueue(additions), 30);
  };

  // Very small parallel-N runner: keeps up to MAX_CONCURRENT uploads in flight
  const runQueue = (newlyAdded) => {
    const pool = newlyAdded || [];
    const startNext = () => {
      setItems((cur) => {
        // Find a queued item we haven't kicked off yet
        const idx = cur.findIndex((it) => it.status === "queued");
        if (idx < 0) return cur;
        activeRef.current += 1;
        const target = cur[idx];
        const next = cur.slice();
        next[idx] = { ...target, status: "uploading" };
        // Kick off the actual upload out of band
        uploadOne(target).finally(() => {
          activeRef.current -= 1;
          startNext();
        });
        return next;
      });
    };
    // Fill the pool up to MAX_CONCURRENT
    const slots = Math.max(0, MAX_CONCURRENT - activeRef.current);
    for (let i = 0; i < slots; i++) startNext();
    // eslint-disable-next-line no-unused-expressions
    pool.length; // silence unused
  };

  const patchItem = (id, patch) => {
    setItems((cur) => cur.map((it) => (it.id === id ? { ...it, ...patch } : it)));
  };

  const uploadOne = async (target) => {
    try {
      if (target.kind === "photo") {
        await uploadPhoto(target, patchItem);
      } else {
        await uploadVideo(target, patchItem);
      }
    } catch (e) {
      patchItem(target.id, { status: "error", error: e?.response?.data?.detail || e.message || "Upload failed" });
    }
  };

  const saveItem = async (it) => {
    if (!it.item_id) return;
    setSaving((s) => ({ ...s, [it.id]: true }));
    try {
      if (it.kind === "photo") {
        await api.post("/marketplace/items/photo-finalize", {
          item_id: it.item_id,
          title: it.title,
          price_cents: parseInt(it.price, 10) || 0,
          size_bytes: it.size,
        });
      } else {
        // Videos: video_id is stashed as item_id (that's the Box video id we got
        // back from complete-upload). Finalize creates the marketplace listing.
        await api.post("/marketplace/items", {
          kind: "video",
          title: it.title,
          price_cents: parseInt(it.price, 10) || 0,
          video_id: it.item_id,
        });
      }
      patchItem(it.id, { status: "saved" });
      toast.success(`${it.title} is live`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving((s) => ({ ...s, [it.id]: false }));
    }
  };

  const removeItem = (id) => {
    setItems((cur) => cur.filter((it) => it.id !== id));
  };

  const retryItem = (id) => {
    setItems((cur) => cur.map((it) => (it.id === id ? { ...it, status: "queued", progress: 0, error: "", item_id: "" } : it)));
    setTimeout(() => runQueue([]), 30);
  };

  const saveAll = async () => {
    const ready = items.filter((it) => it.status === "uploaded" && it.title.trim() && it.price >= 0);
    if (!ready.length) return;
    for (const it of ready) {
      // eslint-disable-next-line no-await-in-loop
      await saveItem(it);
    }
  };

  const readyCount = items.filter((it) => it.status === "uploaded").length;
  const uploadingCount = items.filter((it) => it.status === "uploading" || it.status === "queued").length;
  const savedCount = items.filter((it) => it.status === "saved").length;
  const errorCount = items.filter((it) => it.status === "error").length;

  return (
    <div className="min-h-screen bg-stone-950 text-stone-100 pb-32" data-testid="media-upload-page">
      {/* Sticky header */}
      <div className="sticky top-0 z-30 bg-stone-950/95 backdrop-blur border-b border-stone-800">
        <div className="px-4 py-3 flex items-center justify-between">
          <div>
            <div className="label-tiny text-orange-500">
              {isPhoto ? "Photographer" : isVideo ? "Videographer" : "Media Uploader"}
            </div>
            <div className="font-display uppercase text-lg font-bold leading-tight">
              Upload to Rodeo Cut Studio
            </div>
          </div>
          <button
            onClick={() => { logout(); navigate("/login"); }}
            className="p-2 border border-stone-700 hover:border-orange-500 text-stone-300 hover:text-orange-400"
            title="Sign out"
            data-testid="mediaupload-logout"
          >
            <LogOut size={14} />
          </button>
        </div>
        {items.length > 0 && (
          <div className="px-4 pb-2 flex flex-wrap gap-x-3 gap-y-1 text-[10px] uppercase tracking-widest text-stone-500">
            {uploadingCount > 0 && (
              <span className="text-orange-400 flex items-center gap-1"><Loader2 size={9} className="animate-spin" /> {uploadingCount} uploading</span>
            )}
            {readyCount > 0 && <span className="text-emerald-400">✓ {readyCount} ready</span>}
            {savedCount > 0 && <span className="text-sky-400">{savedCount} live</span>}
            {errorCount > 0 && <span className="text-rose-400">✕ {errorCount} failed</span>}
          </div>
        )}
      </div>

      {/* Default price setter */}
      <div className="px-4 pt-4">
        <div className="border border-stone-800 p-3 mb-4 flex items-center gap-3">
          <DollarSign size={14} className="text-stone-400 shrink-0" />
          <div className="flex-1">
            <div className="text-[10px] uppercase tracking-widest text-stone-500 mb-0.5">Default price per {isPhoto ? "photo" : "video"}</div>
            <div className="flex items-center gap-2">
              <span className="text-stone-500 text-sm">$</span>
              <input
                type="number"
                inputMode="decimal"
                min={0}
                step={1}
                value={(defaultPrice / 100).toFixed(0)}
                onChange={(e) => setDefaultPrice(Math.max(0, Math.round(parseFloat(e.target.value || "0") * 100)))}
                className="bg-transparent border-b border-stone-700 focus:border-orange-500 py-1 w-16 text-sm text-stone-100 focus:outline-none"
                data-testid="mediaupload-default-price"
              />
              <span className="text-[10px] uppercase tracking-widest text-stone-500">CAD</span>
            </div>
          </div>
        </div>
      </div>

      {/* Upload dropzone / picker */}
      <div className="px-4">
        <button
          onClick={pickFiles}
          className="w-full border-2 border-dashed border-orange-700 hover:border-orange-500 bg-orange-600/5 hover:bg-orange-600/10 p-8 text-center transition-colors"
          data-testid="mediaupload-picker"
        >
          <div className="mx-auto h-14 w-14 rounded-full bg-orange-600/20 border border-orange-700 flex items-center justify-center mb-3">
            {isPhoto ? <Camera size={22} className="text-orange-400" /> : <Video size={22} className="text-orange-400" />}
          </div>
          <div className="font-display uppercase text-lg font-bold mb-1">
            Tap to add {isPhoto ? "photos" : "videos"}
          </div>
          <div className="text-xs text-stone-400">
            Pick from your gallery or shoot new ones. Multiple at a time.
          </div>
        </button>
        <input
          ref={fileRef}
          type="file"
          accept={acceptStr}
          multiple
          capture={captureAttr}
          onChange={(e) => { addFiles(e.target.files); e.target.value = ""; }}
          className="hidden"
          data-testid="mediaupload-file-input"
        />
      </div>

      {/* Queue */}
      <div className="px-4 mt-4 space-y-3">
        {items.map((it) => (
          <UploadRow
            key={it.id}
            item={it}
            saving={!!saving[it.id]}
            onChangeTitle={(v) => patchItem(it.id, { title: v })}
            onChangePrice={(v) => patchItem(it.id, { price: v })}
            onSave={() => saveItem(it)}
            onRemove={() => removeItem(it.id)}
            onRetry={() => retryItem(it.id)}
          />
        ))}
      </div>

      {/* Save-all sticky footer */}
      {readyCount > 0 && (
        <div className="fixed bottom-0 inset-x-0 z-40 bg-stone-950/95 backdrop-blur border-t border-stone-800 px-4 py-3">
          <button
            onClick={saveAll}
            className="btn-primary w-full inline-flex items-center justify-center gap-2 text-sm py-3"
            data-testid="mediaupload-save-all"
          >
            <Save size={13} /> Publish all {readyCount} · ${(defaultPrice / 100).toFixed(0)} each
          </button>
        </div>
      )}

      <div className="px-4 mt-6 text-center">
        <Link to="/portal/shooter" className="text-xs text-stone-500 hover:text-orange-400" data-testid="mediaupload-back">
          ← Full shooter portal
        </Link>
      </div>
    </div>
  );
}

function UploadRow({ item, saving, onChangeTitle, onChangePrice, onSave, onRemove, onRetry }) {
  const pct = Math.round(item.progress || 0);
  // Live upload speed + ETA
  const [rate, setRate] = useState(null); // bytes/sec
  const trackRef = useRef({ t: 0, done: 0 });
  useEffect(() => {
    if (item.status !== "uploading") { trackRef.current = { t: 0, done: 0 }; return; }
    const now = Date.now();
    const done = ((item.progress || 0) / 100) * item.size;
    const prev = trackRef.current;
    if (prev.t && now - prev.t > 500) {
      const dt = (now - prev.t) / 1000;
      const bytes = Math.max(0, done - prev.done);
      if (bytes > 0) setRate(bytes / dt);
      trackRef.current = { t: now, done };
    } else if (!prev.t) {
      trackRef.current = { t: now, done };
    }
  }, [item.progress, item.status, item.size]);

  const bytesLeft = Math.max(0, item.size - ((item.progress || 0) / 100) * item.size);
  const etaSec = rate && rate > 0 ? bytesLeft / rate : null;

  return (
    <div
      className={`border p-3 flex gap-3 items-start ${
        item.status === "error" ? "border-rose-900 bg-rose-900/10" :
        item.status === "saved" ? "border-emerald-900 bg-emerald-900/10" :
        "border-stone-800"
      }`}
      data-testid={`mediaupload-row-${item.id}`}
    >
      {/* Thumb */}
      <div className="shrink-0 h-16 w-16 bg-stone-900 border border-stone-800 overflow-hidden flex items-center justify-center">
        {item.thumb ? (
          <img src={item.thumb} alt="" className="w-full h-full object-cover" />
        ) : item.kind === "video" ? (
          <Video size={20} className="text-stone-600" />
        ) : (
          <ImageIcon size={20} className="text-stone-600" />
        )}
      </div>
      {/* Body */}
      <div className="flex-1 min-w-0">
        <input
          type="text"
          value={item.title}
          onChange={(e) => onChangeTitle(e.target.value)}
          maxLength={80}
          disabled={item.status === "saved"}
          className="w-full bg-transparent border-b border-stone-800 focus:border-orange-500 py-1 text-sm text-stone-100 focus:outline-none truncate"
          data-testid={`mediaupload-title-${item.id}`}
        />
        <div className="mt-1 flex items-center gap-3">
          <div className="flex items-center gap-1">
            <span className="text-stone-500 text-xs">$</span>
            <input
              type="number"
              inputMode="decimal"
              min={0}
              step={1}
              value={(parseInt(item.price, 10) / 100).toFixed(0)}
              onChange={(e) => onChangePrice(Math.max(0, Math.round(parseFloat(e.target.value || "0") * 100)))}
              disabled={item.status === "saved"}
              className="bg-transparent border-b border-stone-800 focus:border-orange-500 py-0.5 w-14 text-xs text-stone-100 focus:outline-none"
              data-testid={`mediaupload-price-${item.id}`}
            />
          </div>
          <div className="text-[10px] uppercase tracking-widest text-stone-500">
            {(item.size / (1024 * 1024)).toFixed(1)} MB
          </div>
        </div>
        {/* Status line */}
        <div className="mt-2">
          {item.status === "queued" && (
            <div className="text-[10px] text-stone-500 flex items-center gap-1"><Loader2 size={9} className="animate-spin" /> Waiting…</div>
          )}
          {item.status === "uploading" && (
            <>
              <div className="h-1 bg-stone-900 border border-stone-800 overflow-hidden">
                <div className="h-full bg-orange-500 transition-all" style={{ width: `${pct}%` }} />
              </div>
              <div className="text-[10px] text-stone-500 mt-1 flex items-center gap-2">
                <Loader2 size={9} className="animate-spin" /> {pct}%
                {rate && <span>· {(rate / 1024 / 1024).toFixed(1)} MB/s</span>}
                {etaSec && etaSec > 1 && <span>· {formatEta(etaSec)}</span>}
              </div>
            </>
          )}
          {item.status === "uploaded" && (
            <div className="text-[10px] text-emerald-400 flex items-center gap-1"><Check size={10} /> Uploaded — publish to list it</div>
          )}
          {item.status === "saved" && (
            <div className="text-[10px] text-emerald-400 flex items-center gap-1"><Check size={10} /> Live · buyers can see it now</div>
          )}
          {item.status === "error" && (
            <div className="text-[10px] text-rose-400 flex items-start gap-1"><AlertTriangle size={10} className="shrink-0 mt-0.5" /> {item.error}</div>
          )}
        </div>
      </div>
      {/* Actions */}
      <div className="flex flex-col items-end gap-1 shrink-0">
        {item.status === "uploaded" && (
          <button
            onClick={onSave}
            disabled={saving || !item.title.trim()}
            className="btn-primary !py-1 !px-2 text-[11px] inline-flex items-center gap-1 disabled:opacity-40"
            data-testid={`mediaupload-publish-${item.id}`}
          >
            {saving ? <Loader2 size={10} className="animate-spin" /> : <Check size={11} />}
            Publish
          </button>
        )}
        {item.status === "error" && (
          <button
            onClick={onRetry}
            className="btn-secondary !py-1 !px-2 text-[11px] inline-flex items-center gap-1 !border-orange-600 !text-orange-400"
            data-testid={`mediaupload-retry-${item.id}`}
          >
            <Upload size={11} /> Retry
          </button>
        )}
        {item.status !== "saved" && (
          <button
            onClick={onRemove}
            className="p-1 border border-stone-800 hover:border-rose-500 text-stone-500 hover:text-rose-400"
            title="Remove"
            data-testid={`mediaupload-remove-${item.id}`}
          >
            <Trash2 size={11} />
          </button>
        )}
      </div>
    </div>
  );
}

function FullscreenMsg({ children }) {
  return (
    <div className="min-h-screen bg-stone-950 text-stone-400 flex items-center justify-center text-sm">{children}</div>
  );
}

function RoleGate() {
  return (
    <div className="min-h-screen bg-stone-950 text-stone-100 flex items-center justify-center p-6" data-testid="mediaupload-role-gate">
      <div className="max-w-md text-center border border-rose-900 p-6">
        <AlertTriangle size={28} className="text-rose-400 mx-auto mb-3" />
        <div className="font-display uppercase text-lg font-bold mb-2">Photographer / Videographer only</div>
        <div className="text-sm text-stone-400 mb-4">
          This page is for shooter accounts. If you should have access, ask your admin
          to switch your role to <code className="text-orange-400">photographer</code> or <code className="text-orange-400">videographer</code>.
        </div>
        <Link to="/portal" className="text-xs text-stone-400 hover:text-orange-400">← Back to portal</Link>
      </div>
    </div>
  );
}

function cleanTitle(name) {
  return String(name || "")
    .replace(/\.[a-z0-9]+$/i, "")
    .replace(/[_\-]+/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .slice(0, 80);
}

function formatEta(sec) {
  if (sec < 60) return `${Math.round(sec)}s left`;
  const m = Math.floor(sec / 60);
  const s = Math.round(sec % 60);
  return `${m}m ${s}s left`;
}

// -------- Upload primitives ----------------------------------------------
//
// These match what TikTok / CapCut do:
//   - Small chunks (5 MB) so a flaky moment kills 5 MB not 25
//   - Many chunks in parallel (6 at a time) so bandwidth stays saturated
//   - Retries with exponential backoff so one hiccup doesn't kill the run
//   - Progress aggregated across all in-flight chunks

const CHUNK_SIZE = 5 * 1024 * 1024;   // 5 MB
const CHUNK_CONCURRENCY = 6;
const MAX_RETRIES = 4;                // 4 attempts per chunk

async function uploadPhoto(target, patch) {
  const original = target.file;
  // Shrink big camera photos so they upload fast. Keep the original if
  // compression fails (e.g. HEIC on Chrome) or if the file is already small.
  let file = original;
  try {
    if (original.size > 800 * 1024) {
      const compressed = await compressImage(original, PHOTO_MAX_EDGE, PHOTO_QUALITY);
      if (compressed && compressed.size < original.size) {
        file = compressed;
        patch(target.id, { size: compressed.size });
      }
    }
  } catch { /* fall through with the original */ }
  const ct = file.type || "image/jpeg";
  const init = await api.post("/marketplace/items/photo-init", { content_type: ct });
  const { item_id, upload_url } = init.data;
  await putWithRetry(upload_url, file, ct, (p) => patch(target.id, { progress: p }));
  patch(target.id, { status: "uploaded", progress: 100, item_id });
}

/**
 * Downscale an image to `maxEdge` on its longest side and re-encode as
 * JPEG at the given quality. Runs entirely in the browser using a canvas.
 * Returns a Blob-flavoured File (so it uploads under the original name).
 */
function compressImage(file, maxEdge, quality) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.decoding = "async";
    img.onload = () => {
      URL.revokeObjectURL(url);
      const iw = img.naturalWidth;
      const ih = img.naturalHeight;
      const longest = Math.max(iw, ih);
      // If the source is already smaller than our target, don't upscale
      const scale = longest > maxEdge ? maxEdge / longest : 1;
      const w = Math.round(iw * scale);
      const h = Math.round(ih * scale);
      const canvas = document.createElement("canvas");
      canvas.width = w;
      canvas.height = h;
      const ctx = canvas.getContext("2d");
      // Nicer downscale — smoothing on max
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = "high";
      ctx.drawImage(img, 0, 0, w, h);
      canvas.toBlob(
        (blob) => {
          if (!blob) return reject(new Error("Canvas.toBlob failed"));
          // Wrap in a File so we keep a filename + a matching type
          const jpg = new File([blob], (file.name || "photo").replace(/\.[a-z0-9]+$/i, "") + ".jpg", {
            type: "image/jpeg",
            lastModified: Date.now(),
          });
          resolve(jpg);
        },
        "image/jpeg",
        quality,
      );
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error("Browser cannot decode this image format"));
    };
    img.src = url;
  });
}

async function uploadVideo(target, patch) {
  const file = target.file;
  const ct = file.type || "video/mp4";
  const parts = Math.max(1, Math.ceil(file.size / CHUNK_SIZE));

  const init = await api.post("/box/videos/initiate-upload", {
    filename: file.name, file_size: file.size, content_type: ct, duration: 0,
  });
  const { video_id, upload_id } = init.data;

  // Track per-part progress so the aggregated bar is smooth
  const partProgress = new Array(parts).fill(0);
  const partSizes = new Array(parts).fill(0);
  const etags = new Array(parts).fill(null);
  const reportProgress = () => {
    const total = file.size;
    let done = 0;
    for (let i = 0; i < parts; i++) done += (partProgress[i] / 100) * partSizes[i];
    patch(target.id, { progress: Math.min(99.9, (done / total) * 100) });
  };

  const uploadPart = async (idx) => {
    const start = idx * CHUNK_SIZE;
    const end = Math.min(file.size, start + CHUNK_SIZE);
    const blob = file.slice(start, end);
    partSizes[idx] = blob.size;
    const urlRes = await withApiRetry(
      () => api.post("/box/videos/upload-part-url", {
        video_id, upload_id, part_number: idx + 1,
      }),
      3,
    );
    const put = await putWithRetry(urlRes.data.url, blob, ct, (p) => {
      partProgress[idx] = p;
      reportProgress();
    });
    etags[idx] = { PartNumber: idx + 1, ETag: put.etag };
    partProgress[idx] = 100;
    reportProgress();
  };

  // Parallel-N part uploads
  const queue = Array.from({ length: parts }, (_, i) => i);
  await Promise.all(
    Array.from({ length: Math.min(CHUNK_CONCURRENCY, parts) }, async () => {
      while (queue.length) {
        const idx = queue.shift();
        // eslint-disable-next-line no-await-in-loop
        await uploadPart(idx);
      }
    }),
  );

  await withApiRetry(
    () => api.post("/box/videos/complete-upload", {
      video_id, upload_id, parts: etags,
    }),
    3,
  );
  patch(target.id, { status: "uploaded", progress: 100, item_id: video_id });
}

/**
 * PUT to S3 with progress + retry. On network / 5xx errors we back off
 * (0.5s, 1s, 2s, 4s) and try again up to MAX_RETRIES times before failing.
 * 4xx responses are permanent — don't waste retries on those.
 */
async function putWithRetry(url, blob, contentType, onProgress) {
  let lastErr;
  for (let attempt = 0; attempt < MAX_RETRIES; attempt++) {
    try {
      const r = await xhrPut(url, blob, contentType, onProgress);
      return r;
    } catch (e) {
      lastErr = e;
      // Permanent (4xx) failures — no retry
      if (e.permanent) break;
      // Reset progress to 0 for this chunk before the next attempt so the
      // aggregate bar doesn't jump
      if (onProgress) onProgress(0);
      const backoff = 500 * Math.pow(2, attempt);
      // eslint-disable-next-line no-await-in-loop
      await new Promise((r2) => setTimeout(r2, backoff));
    }
  }
  throw lastErr || new Error("Upload failed after retries");
}

async function withApiRetry(fn, attempts = 3) {
  let lastErr;
  for (let i = 0; i < attempts; i++) {
    try { return await fn(); }
    catch (e) {
      lastErr = e;
      const status = e?.response?.status;
      // Client errors — bail immediately
      if (status && status >= 400 && status < 500) break;
      // eslint-disable-next-line no-await-in-loop
      await new Promise((r) => setTimeout(r, 500 * Math.pow(2, i)));
    }
  }
  throw lastErr;
}

/**
 * XHR-based PUT with a progress callback. We can't use fetch because
 * fetch doesn't expose upload progress. Returns { etag }.
 * Rejects with `err.permanent = true` on 4xx so the retry loop skips them.
 */
function xhrPut(url, blob, contentType, onProgress) {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("PUT", url);
    xhr.setRequestHeader("Content-Type", contentType);
    xhr.timeout = 5 * 60 * 1000; // 5 min per chunk
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable && onProgress) onProgress((e.loaded / e.total) * 100);
    };
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        const etag = (xhr.getResponseHeader("ETag") || "").replaceAll('"', "");
        resolve({ etag });
      } else {
        const err = new Error(`S3 PUT failed (${xhr.status})`);
        // 4xx = permanent (bad signature, expired URL, bad payload, etc.)
        if (xhr.status >= 400 && xhr.status < 500) err.permanent = true;
        reject(err);
      }
    };
    xhr.onerror = () => reject(new Error("Network error uploading to S3"));
    xhr.onabort = () => reject(new Error("Upload aborted"));
    xhr.ontimeout = () => reject(new Error("Upload timed out — check your signal and try again"));
    xhr.send(blob);
  });
}
